/**
 * OF-001E — Workspace Bootstrap Guard (deterministic, no setTimeout).
 *
 * Wraps every provider dashboard. Hard rules:
 *   1. NEVER return `null`.  A blank shell is a user-visible defect.
 *      If the dashboard cannot mount yet, render an inline spinner.
 *   2. Recovery is DETERMINISTIC: a sync `useEffect` runs once when
 *      activeWorkspaceId is null AND there is exactly one provider
 *      candidate.  No `setTimeout(0)`.  No event dispatch.
 *   3. Recovery NEVER overrides an existing activeWorkspaceId.  If
 *      the user picked a workspace via WorkspaceSwitcher, that choice
 *      is the SSOT — the guard renders that choice or waits for it
 *      to hydrate.
 *   4. No `treatx:identity-changed` dispatch.  workspaces[] does not
 *      change on workspace switch — refetching /me only causes races.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRBAC } from '../context/RBACContext';
import { resolveUnambiguousWorkspace } from '../utils/workspaceSelection';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { AlertCircle } from 'lucide-react';

const InlineSpinner = ({ testid }) => (
  <div
    className="min-h-screen flex items-center justify-center bg-slate-50"
    data-testid={testid || 'workspace-bootstrap-spinner'}
  >
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
      <p className="text-sm text-slate-500">Loading workspace…</p>
    </div>
  </div>
);

const WorkspaceBootstrapGuard = ({ children, requireProviderWorkspace = true, allowedRoles }) => {
  const {
    activeWorkspace,
    activeWorkspaceId,
    availableWorkspaces,
    workspaces,
    roleAssignments,
    switchWorkspace,
    contextRequired,
    profileLoaded,
  } = useRBAC();
  const navigate = useNavigate();

  const providerCandidates = (availableWorkspaces || []).filter(
    (w) => w.type === 'provider' && w.clinic_id
  );

  // TREATX-UAT-PROD-CONTEXT-SURFACE-01 — when the SERVER requires a
  // selection this card is the only recovery surface the user reaches
  // (it now also backs every non-dashboard provider route via
  // `RBACProtectedRoute`). It must therefore offer EVERY workspace the
  // account may legitimately select, not just provider-typed ones —
  // otherwise a mixed account (e.g. patient + provider role) sees a
  // card with no way forward. Auto-recovery below stays restricted to
  // the unambiguous single-provider case.
  const pickCandidates = contextRequired
    ? (availableWorkspaces || []).filter((w) => w && w.workspace_id && w.default_route)
    : providerCandidates;

  // TREATX-UAT-PROD-CONTEXT-SURFACE-01b (Codex blocking finding, 2026-09-28).
  //
  // The previous auto-recovery condition equated
  // `providerCandidates.length === 1` with "there is exactly one valid
  // workspace choice". At this shared (role/route-neutral) admission
  // boundary that is UNSAFE: a mixed-role account can hold exactly one
  // provider workspace AND a Patient / TreatX Admin / Accounting
  // workspace, so entering a NON-provider protected route could silently
  // mint the provider context and redirect to the provider
  // `default_route`.
  //
  // Auto-selection is now allowed ONLY when the choice is genuinely
  // unambiguous, derived from EXISTING canonical metadata (the workspace
  // catalogue + `role_assignments` + the route's own `allowedRoles`
  // declaration — no new route→workspace table, no per-URL special
  // cases):
  //   * the SHARED rule in `utils/workspaceSelection` proves the account
  //     has exactly ONE selectable context — i.e. exactly one selectable
  //     catalogue entry AND a catalogue that represents every assignment
  //     the account holds (TREATX-UAT-PROD-CONTEXT-SURFACE-01c: a
  //     one-entry catalogue does NOT prove one persona, because
  //     `treatx_*` and other assignments publish no workspace route),
  //     AND
  //   * the requested route DECLARES its canonical `allowedRoles` and that
  //     workspace's `role_id` is one of them.
  // If the route expresses admission only as a `permission` (no role
  // declaration), eligibility cannot be PROVEN from canonical frontend
  // metadata — so we do not auto-select; the selector is shown instead.
  // Anything else (mixed personas, several providers, several roles at
  // one provider) shows the selector. Selection stays a PROPOSAL: the
  // server validates it in POST /api/auth/unified/context before
  // anything is applied.
  const roleAdmittedByRoute = (ws) =>
    Array.isArray(allowedRoles) && allowedRoles.length > 0
      && allowedRoles.includes(ws.role_id);
  const soleWorkspace = resolveUnambiguousWorkspace(roleAssignments, workspaces);
  const autoTarget =
    soleWorkspace && roleAdmittedByRoute(soleWorkspace) ? soleWorkspace : null;

  // OF-001E §3 — recovery condition.
  //
  // Trigger ONLY when:
  //   - no workspace is currently selected (activeWorkspaceId is null
  //     — NOT just activeWorkspace=null, which can be a transient
  //     filter result while availableWorkspaces refreshes), AND
  //   - the account has exactly ONE selectable workspace AND the
  //     requested route's declared `allowedRoles` admit its role (see
  //     `autoTarget` above — this replaces the unsafe
  //     `providerCandidates.length === 1` rule).
  //
  // We deliberately depend on `activeWorkspaceId` (the raw selection)
  // instead of `activeWorkspace` (the filtered object) — that's what
  // prevents the recovery from clobbering an explicit user choice
  // during a transient `availableWorkspaces` re-derivation.
  // TREATX-UAT-PROD-ADMISSION-RACE-01 — EFFECT GATE. Recovery may not
  // propose anything while the authoritative server state has not been
  // applied (`profileLoaded === false`): the cached snapshot it would
  // reason about is exactly the pre-reconciliation state that produced the
  // admission race, so ZERO context POSTs may originate from it.
  const needsRecovery = profileLoaded && !activeWorkspaceId && !!autoTarget;

  // MTMR-P0-CLOSURE — recovery and manual picks are SERVER-CONFIRMED.
  // `switchWorkspace` performs POST /auth/unified/context first; if the
  // server refuses, nothing is applied and this guard keeps rendering the
  // selection surface. A local-only "authority" transition is no longer
  // possible here.
  useEffect(() => {
    if (!needsRecovery) return;
    let cancelled = false;
    (async () => {
      const applied = await switchWorkspace(autoTarget.workspace_id);
      if (cancelled || !applied?.default_route) return;
      const cur = window.location.hash || '';
      if (!cur.includes(applied.default_route)) {
        navigate(applied.default_route, { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [needsRecovery, autoTarget, switchWorkspace, navigate]);

  // TREATX-UAT-PROD-ADMISSION-RACE-01 — RENDER GATE. Before any
  // pass-through, provider happy path or selector behaviour: while the
  // authoritative state is unknown, render the neutral spinner only.
  if (!profileLoaded) {
    return <InlineSpinner testid="workspace-bootstrap-spinner" />;
  }

  // Pass-through for non-provider surfaces.
  if (!requireProviderWorkspace) return children;

  // Happy path: a provider workspace is active and bound to a clinic AND
  // the server has not invalidated the context (MTMR-P0-CLOSURE).
  if (!contextRequired
    && activeWorkspace && activeWorkspace.type === 'provider' && activeWorkspace.clinic_id) {
    return children;
  }

  // OF-001E §1 — NEVER `return null`. If recovery is in flight or
  // hydration is still settling, show a spinner with a stable
  // data-testid so the FE never renders a blank shell.
  if (!contextRequired && (needsRecovery || (activeWorkspaceId && !activeWorkspace))) {
    return <InlineSpinner testid="workspace-bootstrap-spinner" />;
  }

  // True dead-end: user has no provider workspaces at all or has
  // more than one and no selection — surface an actionable card.
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <Card className="max-w-md w-full border-amber-300 shadow-md" data-testid="workspace-bootstrap-missing">
        <CardContent className="p-6">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-700" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">
                Workspace context missing
              </div>
              <p className="text-xs text-slate-600 mt-1" data-testid="workspace-bootstrap-msg">
                {contextRequired
                  ? 'Select a workspace to continue. Your previous selection is no longer active.'
                  : "We couldn't determine which provider workspace to load. Please pick a workspace from the switcher in the top-right corner to continue."}
              </p>
              {pickCandidates.length >= 1 && (
                <div className="mt-3 space-y-1">
                  {pickCandidates.map((ws) => (
                    <Button
                      key={ws.workspace_id}
                      size="sm"
                      variant="outline"
                      className="w-full justify-start text-xs"
                      data-testid={`workspace-bootstrap-pick-${ws.workspace_id}`}
                      onClick={async () => {
                        const applied = await switchWorkspace(ws.workspace_id);
                        if (applied?.default_route) {
                          navigate(applied.default_route);
                        }
                      }}
                    >
                      {ws.display_name}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkspaceBootstrapGuard;
